import { useEffect, useRef, useState } from "react";
import apiInstance from "../../apiInstance";
import PopupModal from "../Popups/PopupModal";
import PaymentsTable from "./PaymentsTable";

const DevadminPayments = () => {
    const [payments, setPayments] = useState([]);
    const [totalPayments, setTotalPayments] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(50);
    const [selectedItems, setSelectedItems] = useState([]);
    const [bulkAction, setBulkAction] = useState("");
    const [reload, setReload] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filters, setFilters] = useState({
        status: ""
    });
    const [error, setError] = useState("");
    const popupRef = useRef();

    const addPaymentHandler = (payment) => {
        setPayments((prev) => [payment, ...prev]);
    }

    const deletePaymentHandler = (paymentId) => {
		setPayments((prev) => prev.filter(item => item.id !== paymentId));
    }

    const updatePaymentHandler = (payment) => {
        const updatedPayments = payments.map(item => item.id === payment.id ? {...item, ...payment} : item);
        setPayments(updatedPayments)
    }

    const handleSelectAll = (e) => {
        if(e.target.checked) {
            const allIds = payments.map((item) => item._id);
            setSelectedItems(allIds);
        } else {
            setSelectedItems([]);
        }
    }

    const handleSelect = (selectedId) => {
        setSelectedItems(prevSelected => {
            if (prevSelected.includes(selectedId)) {
                return prevSelected.filter(id => selectedId !== id);
            } else {
                return [...prevSelected, selectedId];
            }
        });
    };

    const handleBulkAction = async () => {
        switch(bulkAction) {
            case "delete":
                
                for (const id of selectedItems) {
                    try {
                        const res = await apiInstance.delete(`/devadmin/payment/${id}`);

                        setPayments(prev =>
                            prev.filter(item => item.id !== id)
                        );
                    } catch (error) {
                        console.error(`Error deleting payment:`, error);
                        setError(`Error deleting payment ${id}.`);
                    }
                }
                setReload(prev => prev + 1);
                break;
        }
    }

    const handleCurrentPageChange = (p) => {
        const totalPages = Math.ceil(totalPayments / perPage);
        const value = Math.min(totalPages, Math.max(1, p));
        setCurrentPage(value);
        setSelectedItems([]);
    }

    const renderPaginationControls = () => {

        let totalPages = 0;
        let startItem = 0;
        let endItem = 0;
        if(totalPayments > 0) {
            totalPages = Math.ceil(totalPayments / perPage);
            startItem = (currentPage - 1) * perPage + 1;
            endItem = Math.min(currentPage * perPage, totalPayments);
        }

        return (
            <div className="mjsdev-webs-pagination-wrapper">
                Per Page
                <select 
                    value={perPage} 
                    onChange={(e) => {
                        setPerPage(Number(e.target.value));
                        handleCurrentPageChange(1); 
                    }} 
                >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                </select>
                <span>
                    Showing {startItem}-{endItem} of {totalPayments}
                </span>
                <button onClick={() => handleCurrentPageChange(1)} disabled={currentPage === 1}>
                    &laquo; 
                </button>
                <button 
                    onClick={() => handleCurrentPageChange(currentPage - 1)} 
                    disabled={currentPage === 1}
                >
                    &lsaquo; 
                </button>                
                <input
                    type="number"
                    value={currentPage}
                    onChange={(e) => handleCurrentPageChange(Number(e.target.value))}
                />
                <span>
                    of {totalPages}
                </span>
                <button 
                    onClick={() => handleCurrentPageChange(currentPage + 1)} 
                    disabled={currentPage === totalPages}
                >
                    &rsaquo;
                </button>
                <button onClick={() => handleCurrentPageChange(totalPages)} disabled={currentPage === totalPages}>
                    &raquo;
                </button>
            </div>
        );
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(search || "");
        }, 400); 

        return () => clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        const fetchPayments = async () => {
            apiInstance
                .get(`/devadmin/payment`, {
                    params: {
                        page: currentPage,
                        limit: perPage,
                        search: debouncedSearch,
                        ...filters
                    }
                })
                .then((response) => {
                    const fetchedPayments = response.data.payments || [];
                    setPayments(fetchedPayments);
                    setTotalPayments(response.data.pagination.total || 0);
                    setSelectedItems([]);
                    
                })
                .catch((error) => {
                    console.error("Error fetching data:", error);
                    setError("Failed to load payments.");
                });
        }
        fetchPayments();
		
    }, [currentPage, perPage, reload, filters, debouncedSearch]);

    return (
        <>
            <div className="mjsdev-main">
                <div className="mjsdev-webs-header">
                    <h1></h1>
                    <div>
                        <button 
                            className="mjs-btn"
                            onClick={(e) => {
                                if (popupRef.current) {
                                    popupRef.current.openPopup(
                                        "addPayment"
                                    );
                                }
                            }}
                        >Add New Payment</button>
                    </div>
                </div>

                <div className="mjsdev-webs-toolbar">
                    <div className="mjsdev-webs-bulk_actions">
                        <select
                            value={bulkAction}
                            onChange={(e) => setBulkAction(e.target.value)}
                        >
                            <option value="">Select</option>
                            <option value="delete">Delete</option>
                        </select>
                        <button
                            className="mjs-btn"
                            onClick={handleBulkAction}
                            disabled={!bulkAction || selectedItems.length === 0}
                        >
                            Apply
                        </button>
                    </div>
                    
                    <div className="mjsdev-webs-filters">
                        <div className="mjsdev-search-wrapper">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1); 
                                }}
                            />
                            {search && (
                                <i
                                    className="fa-solid fa-times"
                                    onClick={() => setSearch("")}
                                    title="Clear search"
                                ></i>
                            )}
                        </div>
                        <select
                            value={filters.status}
                            onChange={(e) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    status: e.target.value
                                }))
                                setCurrentPage(1); 
                            }}
                        >
                            <option value="">All Status</option>
                            <option value="paid">Paid</option>
                            <option value="unpaid">Unpaid</option>
                            <option value="failed">Failed</option>
                            <option value="pending">Pending</option>
                            <option value="open">Open</option>
                        </select>
                    </div>

                    <div className="mjsdev-webs-pgn-top">
                        {renderPaginationControls()}
                    </div>
                </div>

                {error && <p className="mjs-error">{error}</p>}

                <PaymentsTable 
                    bulkSelect={true}
                    payments={payments}
                    selectedItems={selectedItems}
                    handleSelectAll={handleSelectAll}
                    handleSelect={handleSelect}
                    onEdit={(payment) => {
                        popupRef.current?.openPopup("editPayment", { payment });
                    }}
                    onDelete={(paymentId) => {
                        popupRef.current?.openPopup("deletePayment", { paymentId });
                    }}
                />
                
                <div className="mjsdev-webs-pgn-bottom">
                    {renderPaginationControls()}
                </div>
            </div>
            <PopupModal
                ref={popupRef}
                addPayment={addPaymentHandler}
                deletePayment={deletePaymentHandler}
                updatePayment={updatePaymentHandler}
            />
        </>
    );
};

export default DevadminPayments;
